import { useState, useEffect, useCallback } from 'react';
import { getVapidPublicKey, subscribeToWebPush, unsubscribeFromWebPush } from '@/app/services/webPushService';

// Helper to convert VAPID key from base64url to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// Get device name for labeling
function getDeviceLabel(): string {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Windows/.test(ua)) return 'Windows';
    return 'Unknown Device';
}

// Check if running on iOS
function isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
           (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// Check if running as installed PWA (standalone mode)
function isStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function usePushNotifications() {
    const [isSupported, setIsSupported] = useState(false);
    const [isIOSBrowser, setIsIOSBrowser] = useState(false);
    const [permission, setPermission] = useState<NotificationPermission>('default');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const isiOSDevice = isIOS();
        const isInstalledPWA = isStandalone();

        // Debug logging
        console.log('Push Debug:', {
            isIOS: isiOSDevice,
            isStandalone: isInstalledPWA,
            hasServiceWorker: 'serviceWorker' in navigator,
            hasPushManager: 'PushManager' in window,
            hasNotification: 'Notification' in window,
            notificationPermission: 'Notification' in window ? Notification.permission : 'N/A'
        });

        // On iOS, push is only supported when installed as PWA
        if (isiOSDevice && !isInstalledPWA) {
            setIsIOSBrowser(true);
            setIsSupported(false);
            console.log('Push: iOS browser mode - not supported');
            return;
        }

        const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
        setIsSupported(supported);
        console.log('Push: Supported =', supported);

        if (supported) {
            setPermission(Notification.permission);
            registerServiceWorker();
        }
    }, []);

    const registerServiceWorker = async () => {
        try {
            console.log('Push: Registering service worker...');
            const reg = await navigator.serviceWorker.register('/service-worker.js');
            console.log('Push: Waiting for service worker to be ready...');
            await navigator.serviceWorker.ready;
            setRegistration(reg);
            console.log('Push: Service worker registered successfully');

            // Check if already subscribed
            const subscription = await reg.pushManager.getSubscription();
            setIsSubscribed(!!subscription);
            console.log('Push: Existing subscription:', !!subscription);
        } catch (err) {
            console.error('Service worker registration failed:', err);
            setError(`Failed to register service worker: ${err}`);
        }
    };

    const subscribe = useCallback(async () => {
        if (!registration) {
            setError('Service worker not ready');
            console.log('Push Subscribe: No registration');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Request notification permission
            console.log('Push Subscribe: Requesting permission...');
            const permissionResult = await Notification.requestPermission();
            setPermission(permissionResult);
            console.log('Push Subscribe: Permission result:', permissionResult);

            if (permissionResult !== 'granted') {
                setError('Notification permission denied');
                setIsLoading(false);
                return;
            }

            // Get VAPID public key from backend
            console.log('Push Subscribe: Fetching VAPID key...');
            const vapidKey = await getVapidPublicKey();
            console.log('Push Subscribe: Got VAPID key:', vapidKey?.substring(0, 20) + '...');

            // Check for existing subscription first
            const existingSubscription = await registration.pushManager.getSubscription();
            console.log('Push Subscribe: Existing subscription:', !!existingSubscription);

            // Subscribe to push notifications
            console.log('Push Subscribe: Subscribing to push manager...');
            const subscription = existingSubscription || await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });
            console.log('Push Subscribe: Got subscription:', !!subscription);

            // Send subscription to backend
            const json = subscription.toJSON();
            console.log('Push Subscribe: Subscription JSON:', JSON.stringify(json).substring(0, 100) + '...');

            if (json.endpoint && json.keys) {
                console.log('Push Subscribe: Sending to backend...');
                await subscribeToWebPush(
                    json.endpoint,
                    json.keys.p256dh || '',
                    json.keys.auth || '',
                    getDeviceLabel(),
                    'manager'
                );
                setIsSubscribed(true);
                console.log('Push Subscribe: Success!');
            } else {
                setError('Invalid subscription data');
                console.error('Push Subscribe: Invalid subscription JSON:', json);
            }
        } catch (err) {
            console.error('Push subscription failed:', err);
            setError(`Failed to enable notifications: ${err}`);
        } finally {
            setIsLoading(false);
        }
    }, [registration]);

    const unsubscribe = useCallback(async () => {
        if (!registration) return;

        setIsLoading(true);
        setError(null);

        try {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                await subscription.unsubscribe();
                await unsubscribeFromWebPush();
            }
            setIsSubscribed(false);
        } catch (err) {
            console.error('Unsubscribe failed:', err);
            setError('Failed to disable notifications');
        } finally {
            setIsLoading(false);
        }
    }, [registration]);

    // Sync existing subscription with current user (call on login)
    const syncSubscription = useCallback(async () => {
        if (!registration || !isSupported) return;

        try {
            const subscription = await registration.pushManager.getSubscription();
            if (subscription) {
                // Re-register existing subscription with backend to update user_id
                console.log('Push Sync: Re-registering subscription for current user...');
                const json = subscription.toJSON();
                if (json.endpoint && json.keys) {
                    await subscribeToWebPush(
                        json.endpoint,
                        json.keys.p256dh || '',
                        json.keys.auth || '',
                        getDeviceLabel(),
                        'manager'
                    );
                    console.log('Push Sync: Subscription synced to current user');
                }
            }
        } catch (err) {
            console.error('Push Sync failed:', err);
        }
    }, [registration, isSupported]);

    return {
        isSupported,
        isIOSBrowser,
        permission,
        isSubscribed,
        isLoading,
        error,
        subscribe,
        unsubscribe,
        syncSubscription
    };
}
